import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";

const CLOSE_DRAG_PX = 72;
const CLOSE_VELOCITY = 0.45;

type DragMode = "undecided" | "sheet" | "scroll";

export interface SwipeDismissSheetResult {
  dragY: number;
  isDragging: boolean;
  panelStyle: CSSProperties;
  scrollRef: RefObject<HTMLDivElement>;
  dragHandleProps: {
    onPointerDown: (e: ReactPointerEvent) => void;
    onPointerMove: (e: ReactPointerEvent) => void;
    onPointerUp: (e: ReactPointerEvent) => void;
    onPointerCancel: (e: ReactPointerEvent) => void;
  };
  scrollBodyProps: {
    onPointerDown: (e: ReactPointerEvent) => void;
    onPointerMove: (e: ReactPointerEvent) => void;
    onPointerUp: (e: ReactPointerEvent) => void;
    onPointerCancel: (e: ReactPointerEvent) => void;
    style?: CSSProperties;
  };
}

/**
 * Swipe-down-to-dismiss for mobile bottom sheets (Traders-style grip + drag).
 * Attach dragHandleProps to the grip/header; scrollBodyProps to the scroll region.
 */
export function useSwipeDismissSheet(
  enabled: boolean,
  onClose: () => void
): SwipeDismissSheetResult {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const dragStartY = useRef(0);
  const dragStartTime = useRef(0);
  const activePointer = useRef<number | null>(null);
  const dragMode = useRef<DragMode>("undecided");

  const resetDrag = useCallback(() => {
    activePointer.current = null;
    dragMode.current = "undecided";
    setIsDragging(false);
  }, []);

  const finishDrag = useCallback(
    (deltaY: number, elapsedMs: number) => {
      const velocity = deltaY / Math.max(elapsedMs, 1);
      if (deltaY > CLOSE_DRAG_PX || velocity > CLOSE_VELOCITY) {
        onClose();
        return;
      }
      setDragY(0);
    },
    [onClose]
  );

  const canStartSheetDrag = useCallback(() => {
    const el = scrollRef.current;
    return !el || el.scrollTop <= 0;
  }, []);

  // When the sheet is disabled (closed / off mobile), zero out any leftover drag
  // offset so a stale translate can't stick on a panel that stays mounted and
  // reopens later.
  useEffect(() => {
    if (!enabled) {
      resetDrag();
      setDragY(0);
    }
  }, [enabled, resetDrag]);

  const onDragZonePointerDown = useCallback(
    (e: ReactPointerEvent) => {
      if (!enabled || e.button !== 0) return;
      // A press that starts on an interactive control (e.g. the sheet's close
      // button inside a draggable header) is a tap, not a sheet drag. Taking
      // pointer capture here would retarget the pointerup to the drag zone and
      // the browser would then never deliver the control's click.
      const target = e.target as HTMLElement | null;
      if (target && target.closest("button, a, input, select, textarea, [role='button']")) {
        return;
      }
      activePointer.current = e.pointerId;
      dragMode.current = "sheet";
      dragStartY.current = e.clientY;
      dragStartTime.current = performance.now();
      setIsDragging(true);
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [enabled]
  );

  const onDragZonePointerMove = useCallback(
    (e: ReactPointerEvent) => {
      if (!enabled || activePointer.current !== e.pointerId) return;
      if (dragMode.current === "scroll") return;
      const deltaY = e.clientY - dragStartY.current;
      if (deltaY > 0) setDragY(deltaY);
      e.preventDefault();
    },
    [enabled]
  );

  const onDragZonePointerUp = useCallback(
    (e: ReactPointerEvent) => {
      if (!enabled || activePointer.current !== e.pointerId) return;
      if (dragMode.current === "sheet") {
        const deltaY = e.clientY - dragStartY.current;
        finishDrag(deltaY, performance.now() - dragStartTime.current);
      }
      resetDrag();
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* already released */
      }
    },
    [enabled, finishDrag, resetDrag]
  );

  const onContentPointerDown = useCallback(
    (e: ReactPointerEvent) => {
      if (!enabled) return;
      if (!canStartSheetDrag()) return;
      activePointer.current = e.pointerId;
      dragMode.current = "undecided";
      dragStartY.current = e.clientY;
      dragStartTime.current = performance.now();
    },
    [canStartSheetDrag, enabled]
  );

  const onContentPointerMove = useCallback(
    (e: ReactPointerEvent) => {
      if (!enabled || activePointer.current !== e.pointerId) return;
      const deltaY = e.clientY - dragStartY.current;

      if (dragMode.current === "undecided") {
        if (Math.abs(deltaY) < 6) return;
        if (deltaY > 0 && canStartSheetDrag()) {
          dragMode.current = "sheet";
          setIsDragging(true);
          e.currentTarget.setPointerCapture(e.pointerId);
          setDragY(deltaY);
        } else {
          dragMode.current = "scroll";
          activePointer.current = null;
          return;
        }
      }

      if (dragMode.current !== "sheet") return;
      if (deltaY > 0) setDragY(deltaY);
      e.preventDefault();
    },
    [canStartSheetDrag, enabled]
  );

  const onContentPointerUp = useCallback(
    (e: ReactPointerEvent) => {
      if (!enabled || activePointer.current !== e.pointerId) return;
      if (dragMode.current === "sheet") {
        const deltaY = e.clientY - dragStartY.current;
        finishDrag(deltaY, performance.now() - dragStartTime.current);
      }
      resetDrag();
    },
    [enabled, finishDrag, resetDrag]
  );

  const transition =
    isDragging || !enabled
      ? "none"
      : "transform 280ms cubic-bezier(0.32, 0.72, 0, 1)";

  return {
    dragY,
    isDragging,
    panelStyle: enabled
      ? {
          transform: `translateY(${dragY}px)`,
          transition,
          touchAction: "none",
        }
      : {},
    scrollRef,
    dragHandleProps: {
      onPointerDown: onDragZonePointerDown,
      onPointerMove: onDragZonePointerMove,
      onPointerUp: onDragZonePointerUp,
      onPointerCancel: onDragZonePointerUp,
    },
    scrollBodyProps: {
      onPointerDown: onContentPointerDown,
      onPointerMove: onContentPointerMove,
      onPointerUp: onContentPointerUp,
      onPointerCancel: onContentPointerUp,
      style: enabled && isDragging ? { overflowY: "hidden" } : undefined,
    },
  };
}
